/**
 * SafeHtml - Renderiza HTML sanitizado da Timeline V2 de tickets (editor rico).
 *
 * Allowlist ESPELHADA da sanitização server-side (`sanitize-html`, ver
 * `TROIA-V3-BACKEND/src/modules/tickets/html-sanitizer.ts`
 * `TICKET_HTML_ALLOWLIST`) — a defesa REAL contra XSS é sempre o sanitizer
 * do backend no WRITE (o cliente pode bypassar DOMPurify chamando a API
 * direto); este componente é só UX no READ, evitando re-renderizar HTML
 * inconsistente.
 *
 * Tags:  p, br, strong, b, em, i, u, s, a, ul, ol, li, img, pre, code, blockquote
 * Attrs: a[href,target,rel], img[src,width,height,alt]
 *
 * Usado para:
 * - `TicketTimelineItem.content` com `contentFormat: 'html'` (activity comment
 *   ou nota interna vindos do editor rico)
 * - Corpo de e-mails OUTBOUND (`TimelineEmailItem`, `content`/`contentFormat`)
 *
 * NÃO usar para `contentFormat: 'text'` (ou ausente) — itens legados são
 * sempre texto puro (`whitespace-pre-wrap`), NUNCA interpretados como HTML.
 * NÃO usar para e-mails INBOUND (`emailHtml`) — HTML externo/adversarial usa
 * o iframe sandboxed `EmailHtmlFrame` (isolamento total, não allowlist).
 */
import React from 'react';
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'a',
  'ul', 'ol', 'li', 'img', 'pre', 'code', 'blockquote',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'width', 'height', 'alt'];

/**
 * Sanitiza com a allowlist E força `target="_blank"` + `rel="noopener
 * noreferrer"` em todo `<a>` — espelhando o `transformTags` do backend (que
 * nunca confia no target/rel enviado pelo cliente). O pós-processamento é
 * TODO escopado nesta função (fix pós-review M3): sanitize com
 * `RETURN_DOM_FRAGMENT`, ajuste dos `<a>` no fragmento sanitizado e
 * serialização via container efêmero — SEM `DOMPurify.addHook` global (o
 * singleton compartilhado do DOMPurify não pode ser mutado por import de
 * módulo; afetaria outros consumidores do app).
 */
function sanitizeTimelineHtml(html: string): string {
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    RETURN_DOM_FRAGMENT: true,
  });

  fragment.querySelectorAll('a').forEach((anchor) => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
}

export interface SafeHtmlProps {
  html: string;
  className?: string;
  'data-testid'?: string;
}

export const SafeHtml: React.FC<SafeHtmlProps> = ({ html, className, ...rest }) => {
  const sanitized = React.useMemo(() => sanitizeTimelineHtml(html), [html]);

  return (
    <div
      className={className}
      data-testid={rest['data-testid'] ?? 'ticket-timeline-safe-html'}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

SafeHtml.displayName = 'SafeHtml';
